const reducedMotion = window.matchMedia(
	'(prefers-reduced-motion: reduce)',
).matches;

const selectors = {
	answer: '.schema-faq-answer',
	container: '.wp-block-yoast-faq-block',
	question: '.schema-faq-question',
	faq: '.schema-faq-section',
};

const transitionListeners = new WeakMap();

class YoastFaq {
	constructor(faqs, faq, index) {
		this.allFaqs = faqs;
		this.faq = faq;
		this.index = index;
		this.question = faq.querySelector(selectors.question);
		this.answer = faq.querySelector(selectors.answer);

		if (!this.question || !this.answer) {
			return;
		}

		this.initialize();
	}

	/**
	 * Initialize events for each FAQ
	 */
	initialize() {
		if (this.question.tagName.toLowerCase() !== 'button') {
			const button = this.convertQuestionToButton();
			this.question.replaceWith(button);
			this.question = button;
		}

		this.setup();
		this.questionClickListener();
	}

	clearAnswerTransitionListener(panel) {
		const listener = transitionListeners.get(panel);
		if (listener) {
			panel.removeEventListener('transitionend', listener);
			transitionListeners.delete(panel);
		}
	}

	/**
	 * Skips the current FAQ, but closes the rest
	 */
	closeAllFaqsExceptForCurrentOne() {
		this.allFaqs.forEach((faq) => {
			if (faq === this.faq) {
				return;
			}

			const question = faq.querySelector(selectors.question);
			const answer = faq.querySelector(selectors.answer);

			if (
				question &&
				answer &&
				question.getAttribute('aria-expanded') === 'true'
			) {
				this.collapse(question, answer, faq);
			}
		});
	}

	/**
	 * Collapses the FAQ
	 * If reduced motion is preferred, it immediately closes the FAQ and exits early
	 * Otherwise it transitions to close it
	 *
	 * @param {HTMLElement} question
	 * @param {HTMLElement} answer
	 * @param {HTMLElement} faq
	 */
	collapse(question, answer, faq) {
		this.clearAnswerTransitionListener(answer);

		question.setAttribute('aria-expanded', 'false');
		faq.classList.remove('is-open');

		if (reducedMotion) {
			answer.setAttribute('data-state', 'closed');
			answer.style.display = 'none';
			return;
		}

		const current = answer.scrollHeight;
		answer.style.maxHeight = current + 'px';
		void answer.offsetHeight;

		answer.setAttribute('data-state', 'closing');
		answer.style.maxHeight = '0px';

		const onEnd = (e) => {
			if (e.propertyName !== 'max-height') {
				return;
			}

			answer.setAttribute('data-state', 'closed');
			this.clearAnswerTransitionListener(answer);
		};

		transitionListeners.set(answer, onEnd);
		answer.addEventListener('transitionend', onEnd);
	}

	/**
	 * If the question tag is not a button, we must convert it to be one
	 */
	convertQuestionToButton() {
		const button = document.createElement('button');
		button.type = 'button';
		button.className = this.question.className;
		button.innerHTML = this.question.innerHTML;

		return button;
	}

	/**
	 * Expands the FAQ
	 * If reduced motion is preferred, it immediately opens the FAQ and exits early
	 * Otherwise it transitions to open it
	 *
	 * @param {HTMLElement} question
	 * @param {HTMLElement} answer
	 * @param {HTMLElement} faq
	 */
	expand(question, answer, faq) {
		this.clearAnswerTransitionListener(answer);

		question.setAttribute('aria-expanded', 'true');
		faq.classList.add('is-open');

		if (reducedMotion) {
			answer.style.maxHeight = '';
			answer.setAttribute('data-state', 'open');
			return;
		}

		answer.setAttribute('data-state', 'opening');
		answer.style.display = 'block';
		answer.style.maxHeight = answer.scrollHeight + 'px';

		const onEnd = (e) => {
			if (e.propertyName !== 'max-height') {
				return;
			}

			answer.style.maxHeight = '';
			answer.style.display = '';
			answer.setAttribute('data-state', 'open');
			this.clearAnswerTransitionListener(answer);
		};

		transitionListeners.set(answer, onEnd);
		answer.addEventListener('transitionend', onEnd);
	}

	/**
	 * Handles click event for current FAQ question.
	 * If it's already expanded, it closes the current FAQ
	 * Otherwise, it closes all other FAQs and expands the current one.
	 */
	questionClickListener() {
		this.question.addEventListener('click', () => {
			const isExpanded = this.question.getAttribute('aria-expanded') === 'true';
			if (isExpanded) {
				this.collapse(this.question, this.answer, this.faq);
				return;
			}

			this.closeAllFaqsExceptForCurrentOne();

			this.expand(this.question, this.answer, this.faq);
		});
	}

	/**
	 * Sets up initial classes, attributes, and IDs on page load
	 */
	setup() {
		this.faq.classList.remove('is-open');
		this.answer.classList.add('yoast-faq-panel');
		this.answer.setAttribute('data-state', 'closed');
		this.answer.style.maxHeight = '0px';
		this.question.setAttribute('aria-expanded', 'false');

		const id = this.faq.id
			? this.faq.id + '-answer'
			: `yoast-faq-${this.index}-answer`;
		this.answer.id = id;
		this.question.setAttribute('aria-controls', id);
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const faqContainers = document.querySelectorAll(selectors.container);
	if (faqContainers.length === 0) {
		return;
	}

	faqContainers.forEach((container) => {
		const faqs = container.querySelectorAll(selectors.faq);
		if (faqs.length === 0) {
			return;
		}

		faqs.forEach((faq, index) => {
			new YoastFaq(faqs, faq, index);
		});
	});
});
